/**
 * Features Settings Tab Component
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Zap, FileText, FileBadge, XCircle, ChevronRight, LayoutGrid, CalendarRange, Gauge } from 'lucide-react';
import { PlatformSettings, BarrattFlags } from './types';

interface FeaturesTabProps {
  platformSettings: PlatformSettings;
  barrattFlags: BarrattFlags;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onBarrattFlagsChange: (flags: BarrattFlags) => void;
  onSave: () => void;
}

export function FeaturesTab({
  platformSettings,
  barrattFlags,
  saving,
  onPlatformSettingsChange,
  onBarrattFlagsChange,
  onSave,
}: FeaturesTabProps) {
  const updatePlatform = (updates: Partial<PlatformSettings>) => {
    onPlatformSettingsChange({ ...platformSettings, ...updates });
  };

  const updateBarratt = (updates: Partial<BarrattFlags>) => {
    onBarrattFlagsChange({ ...barrattFlags, ...updates });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Feature Settings
          </CardTitle>
          <CardDescription>
            Toggle sales orders + delivery workflow behaviour and portal-mode flags for this tenant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Purchasing Workflow */}
          <div>
            <h3 className="font-semibold mb-4">Sales Orders Workflow</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<XCircle className="h-5 w-5 text-primary" />}
                label="Allow Supplier Decline"
                description="Allow suppliers to decline (reject) new PO lines. When disabled, suppliers can only accept or propose changes."
                checked={barrattFlags.allow_customer_decline}
                onChange={(checked) => updateBarratt({ allow_customer_decline: checked })}
              />
              <FeatureToggle
                icon={<CalendarRange className="h-5 w-5 text-primary" />}
                label="SO Line Schedules"
                description="Enable schedule-level tracking for PO lines. Shows a dedicated SO Line Schedules page with delivery schedule breakdowns per line."
                checked={platformSettings.poLineSchedules}
                onChange={(checked) => updatePlatform({ poLineSchedules: checked })}
              />
              <FeatureToggle
                icon={<FileBadge className="h-5 w-5 text-primary" />}
                label="Job Documents"
                description="Show the Certificate of Conformity and Despatch Note on order lines, generated from imported ERP job records. Off by default: these are certified documents that go to a customer, so turn them on only once the job data behind them has been checked."
                checked={platformSettings.jobDocuments}
                onChange={(checked) => updatePlatform({ jobDocuments: checked })}
              />
            </div>
          </div>

          {/* Quality Measure */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">Quality Measure</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<Gauge className="h-5 w-5 text-primary" />}
                label="Use PPK instead of PPM"
                description="When on, defect-rate metrics across dashboards, drill-downs and exports switch from Parts Per Million to Parts Per Thousand (rejected ÷ received × 1,000). Off = PPM (default)."
                checked={platformSettings.qualityMeasurePPK}
                onChange={(checked) => updatePlatform({ qualityMeasurePPK: checked })}
              />
            </div>
          </div>

          {/* NCR Settings */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">NCR (Non-Conformance Report) Settings</h3>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base font-medium">NCR Progress Template</Label>
                    <p className="text-sm text-muted-foreground">
                      Choose the visual progress indicator style for NCR workflow
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div
                    onClick={() => updatePlatform({ ncrTemplate: 1 })}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      platformSettings.ncrTemplate === 1 ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${platformSettings.ncrTemplate === 1 ? 'border-primary bg-primary' : 'border-border'}`}>
                        {platformSettings.ncrTemplate === 1 && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-white" /></div>}
                      </div>
                      <Label className="font-semibold">Template 1: Mandatory 8D</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Classic 4-step linear progress with role badges</p>
                    <div className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-white">1</div><span>Created</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white">2</div><span>CA</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">3</div><span>CAR</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">4</div><span>Close</span></div>
                    </div>
                  </div>

                  <div
                    onClick={() => updatePlatform({ ncrTemplate: 2 })}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      platformSettings.ncrTemplate === 2 ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${platformSettings.ncrTemplate === 2 ? 'border-primary bg-primary' : 'border-border'}`}>
                        {platformSettings.ncrTemplate === 2 && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-white" /></div>}
                      </div>
                      <Label className="font-semibold">Template 2: Optional 8D</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Modern emoji-based dynamic steps (8D, Collection, Credit)</p>
                    <div className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                      <div className="flex flex-col items-center"><span className="text-lg">📋</span><span>Created</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center"><span className="text-lg">⚠️</span><span>8D</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center opacity-50"><span className="text-lg">📦</span><span>Collect</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center"><span className="text-lg">✅</span><span>Closed</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Button onClick={onSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Feature Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Optional Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Optional Modules
          </CardTitle>
          <CardDescription>
            Portal mode and navigation presets — controls whether this tenant uses the standard Helix layout, the compliance-only layout, or a customer-portal layout.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Portal Mode Selector */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-accent/20">
            <div className="flex items-center gap-3">
              <LayoutGrid className="h-5 w-5 text-primary" />
              <div>
                <Label className="text-base font-medium">Navigation Mode</Label>
                <p className="text-sm text-muted-foreground">Choose the sidebar navigation layout style</p>
              </div>
            </div>
            <Select
              value={barrattFlags.portal_mode}
              onValueChange={(value: 'standard' | 'compliance' | 'supplier_portal') => updateBarratt({ portal_mode: value })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (Helix)</SelectItem>
                <SelectItem value="compliance">Compliance Mode</SelectItem>
                <SelectItem value="supplier_portal">Supplier Portal Mode</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={onSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Optional Modules'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

interface FeatureToggleProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function FeatureToggle({ icon, label, description, checked, onChange }: FeatureToggleProps) {
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <Label className="text-base font-medium">{label}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
